import { IntegrationAccount } from "@common/types/integrationAccount";
import { PlaidConnection } from "@common/types/plaidConnection";
import classNames from "classnames";
import React from "react";
import { useFinancialAccountEntitySelection } from "@hooks/useFinancialAccountEntitySelection";
import { AccountConnectionRow } from "./AccountConnectionRow";
import { useAccountsConnectionContext } from "./AccountsConnectionContext";
import styles from "./AccountsConnectionWidget.module.scss";
import { isValidAccount } from "./helpers";

interface AccountConnectionWidgetProps {
    heading: React.ReactNode;
    accounts: IntegrationAccount[];
    connection?: PlaidConnection;
}

export const AccountsConnectionWidget: React.FC<
    AccountConnectionWidgetProps
> = ({ heading, accounts, connection }) => {
    const { showBusinessEntitySelection, showPersonalEntitySelection } =
        useFinancialAccountEntitySelection();
    const entitySelection =
        showBusinessEntitySelection || showPersonalEntitySelection;
    const cols = entitySelection ? 3 : 2;
    const { showAccountTypeWarning } = useAccountsConnectionContext();
    const hasError =
        showAccountTypeWarning && accounts.some((a) => !isValidAccount(a));

    return (
        <table
            className={classNames("table", styles.root, {
                [styles.error]: hasError,
            })}
        >
            <thead>
                <tr>
                    <th colSpan={cols} className="p-0">
                        <div className={styles.header}>
                            <header>{heading}</header>
                        </div>
                    </th>
                </tr>
                {entitySelection && accounts.length > 0 && (
                    <tr
                        className={classNames(
                            "small",
                            "text-grey",
                            styles.headerLabels,
                        )}
                    >
                        <th>Account</th>
                        <th className={styles.classification}>Type</th>
                        <th>Entity / Business</th>
                    </tr>
                )}
            </thead>

            <tbody>
                {accounts.map(
                    (account) =>
                        account.financialAccount && (
                            <AccountConnectionRow
                                key={account.id}
                                integrationAccount={account}
                                financialAccount={account.financialAccount}
                                connection={connection}
                            />
                        ),
                )}
            </tbody>
        </table>
    );
};
